import Experiments from '@wix/wix-experiments';
import { merge } from 'lodash';
import { ViewPresets, ViewPreset, PRESETS } from './presets';
import { CONDUCTOR_FT, EXPERIMENTS } from '../stores/constants';
import {
  isPostLoginPath,
  isPasskeyPostLoginPath,
  isEnforcementPostLoginPath,
} from '../routes';
import { SEARCH_PARAMS } from './constants';

export const WIX_2025_BRAND_USER_TYPES = ['business-launcher'];

export const MAP_VERTICAL_PATH_TO_PRESET = {
  logo: PRESETS.LOGO_MAKER,
  [PRESETS.DAYFUL]: PRESETS.DAYFUL,
  [PRESETS.CODUX]: PRESETS.CODUX,
  [PRESETS.HOPP]: PRESETS.HOPP,
  [PRESETS.WIXEL]: PRESETS.WIXEL,
  [PRESETS.NAUTILUS]: PRESETS.NAUTILUS,
  [PRESETS.SYMPHONY]: PRESETS.SYMPHONY,
};

type PresetResolutionParams = {
  getQueryParam: (key: string) => string;
  baseDomain?: string;
  userType?: string;
  pathname?: string;
  experiments: Experiments;
};

export type ResolvedPreset = {
  presetKey?: string;
  preset?: ViewPreset;
};

export const resolvePresetKey = ({
  getQueryParam,
  baseDomain,
  userType,
  pathname,
  experiments,
}: PresetResolutionParams): string | undefined => {
  const getPresetFromQueryParams = () =>
    getQueryParam('preset') ||
    getQueryParam('customized') ||
    getQueryParam('color');

  // Branded routes are mounted at `/{vertical}/signin` (e.g. `/symphony/signin`),
  // so the first path segment identifies the vertical. Resolving from it lets a
  // branded route apply its preset purely on the frontend — without depending on
  // the backend populating `userType` for that vertical.
  const verticalFromPath = pathname?.split('/').filter(Boolean)[0];

  const shouldPrioritizeUserType = experiments.enabled(
    'specs.ident.prioritizeUserTypeOverQueryParam',
  );

  let presetKey: string | undefined;

  if (shouldPrioritizeUserType) {
    if (userType && MAP_VERTICAL_PATH_TO_PRESET[userType]) {
      presetKey = MAP_VERTICAL_PATH_TO_PRESET[userType];
    } else {
      presetKey = getPresetFromQueryParams();
    }
  } else {
    presetKey = getPresetFromQueryParams();
  }

  if (!presetKey) {
    // Special handling for editorX
    if (baseDomain?.includes('editorx')) {
      presetKey = PRESETS.EDITORX;
    } else if (userType && MAP_VERTICAL_PATH_TO_PRESET[userType]) {
      presetKey = MAP_VERTICAL_PATH_TO_PRESET[userType];
    } else if (
      verticalFromPath &&
      MAP_VERTICAL_PATH_TO_PRESET[verticalFromPath]
    ) {
      // Fall back to the URL path segment (replaces a legacy parse of `origin`,
      // which never contains the path and so never matched).
      presetKey = MAP_VERTICAL_PATH_TO_PRESET[verticalFromPath];
    }
  }

  // Normalize the empty string from getQueryParam() to undefined so callers get
  // a clean "no preset" signal.
  return presetKey || undefined;
};

export const resolvePreset = (
  params: PresetResolutionParams,
): ResolvedPreset => {
  const presetKey = resolvePresetKey(params);

  // Resolve preset from ViewPresets, trying exact match and case variations
  let preset: ViewPreset = {};
  let resolvedPresetKey: string | undefined = presetKey;
  if (presetKey) {
    preset =
      ViewPresets[presetKey] ||
      ViewPresets[presetKey.toLowerCase()] ||
      ViewPresets[presetKey.toUpperCase()] ||
      {};
    // If presetKey was set but doesn't resolve to a valid preset, treat it as undefined
    if (Object.keys(preset).length === 0) {
      console.log(
        '[PresetDetection] Preset key from URL path does not resolve to valid preset:',
        presetKey,
      );
      resolvedPresetKey = undefined;
    } else {
      console.log('[PresetDetection] Resolved preset from key:', presetKey);
    }
  }
  // If no preset was explicitly set, use wix-2025 as default.
  // Post-login routes are excluded, except passkey enroll which uses the wix-2025 layout.
  // Controlled by two experiments: one for regular Wix users, one for Studio and brand user types.
  const isPostLoginRoute =
    params.pathname !== undefined && isPostLoginPath(params.pathname);
  const isPasskeyPostLoginRoute =
    params.pathname !== undefined &&
    isPasskeyPostLoginPath(params.pathname, params.getQueryParam('debugCase'));
  const isEnforcementRoute =
    params.pathname !== undefined &&
    isEnforcementPostLoginPath(params.pathname);
  // Enforcement routes join the wix-2025 preset only when their own FT is on
  // (evaluated only on an enforcement route, so it never affects auth screens).
  // When it's off they're excluded like any not-yet-redesigned post-login screen.
  const isExcludedFromWix2025 =
    isPostLoginRoute &&
    !isPasskeyPostLoginRoute &&
    !(
      isEnforcementRoute &&
      params.experiments.get(
        EXPERIMENTS.SHOULD_ACTIVATE_WIX_2025_ENFORCEMENT_REDESIGN,
      ) === CONDUCTOR_FT.ENABLED
    );

  if (!resolvedPresetKey && !isExcludedFromWix2025) {
    const isWix2025ExperimentEnabled = params.experiments.enabled(
      EXPERIMENTS.SHOULD_ACTIVATE_WIX_2025_PRESET,
    );
    const isBrandsExperimentEnabled = params.experiments.enabled(
      EXPERIMENTS.SHOULD_ACTIVATE_WIX_2025_FOR_BRANDS,
    );
    const isStudio = params.getQueryParam(SEARCH_PARAMS.IS_STUDIO) === 'true';
    const isWix2025Brand =
      isStudio || WIX_2025_BRAND_USER_TYPES.includes(params.userType ?? '');

    const shouldApplyWix2025 =
      isWix2025ExperimentEnabled ||
      (isBrandsExperimentEnabled && isWix2025Brand);

    if (shouldApplyWix2025) {
      console.log('[PresetDetection] Applying wix-2025 preset');
      resolvedPresetKey = PRESETS.WIX_2025;
      preset = ViewPresets[PRESETS.WIX_2025] || {};
    }
  }

  return {
    presetKey: resolvedPresetKey,
    preset,
  };
};

export const detectPreset = (experiments: Experiments): ResolvedPreset => {
  const urlParams = new URLSearchParams(window.location.search);
  return resolvePreset({
    getQueryParam: (key) => urlParams.get(key) ?? '',
    baseDomain: window.__BASE_DOMAIN__,
    userType: window.__USER_TYPE__ || '',
    pathname: window.location.pathname,
    experiments,
  });
};

export const applyPartnerOverride = (
  preset: ViewPreset,
  partnerKey: string | undefined,
): ViewPreset => {
  if (!partnerKey) {
    return preset;
  }
  const partnerOverride = preset.partners?.[partnerKey];
  if (!partnerOverride) {
    console.log('[PresetDetection] Unknown partner key:', partnerKey);
    return preset;
  }
  return merge({}, preset, partnerOverride);
};
